
import { Component, Vue, Watch } from 'vue-property-decorator'
import { ListDepts, ListProjects } from '@/api/orgAdmin'
import { ListOrgUsers } from '@/api/orgAdmin'
import { IAllDeptInfo, IAllUserInfo, DeptType, IProject, IDept, LeakStatus } from '@/api/types'
import { BatchAssignRepairDept, ListLeaks } from '@/api/leaks'

// 导入组件
import LeakList from '@/views/leaks/components/LeakList/index.vue'
import SearchFilter from '@/views/leaks/components/SearchFilter/index.vue'
import DayToDayFilter from '@/views/leaks/components/DayToDayFilter/index.vue'
import { GetLeakStatusColor } from '@/utils/statusUi'
import { UserModule } from '@/store/modules/user'
import { HasPer, IsMobile } from '@/utils/check'

// 卡片数据接口
interface CardData {
  id: string
  title: string
  filterParams: any
  todayCount: number
  yesterdayCount: number
  startDate: string
  color: string
}

@Component({
  name: 'DetectorLeakIndex',
  components: {
    LeakList,
    SearchFilter,
    DayToDayFilter
  }
})
export default class extends Vue {
  // 对话框相关
  private createDialogVisible = false
  private shouldReloadList = true

  // 计算属性：是否为移动端
  get isMobile() {
    return IsMobile()
  }

  // 创建漏点相关
  private createTemp = {
    location: {
      longitude: 0,
      latitude: 0
    },
    referenceAddr: ''
  }

  // 计算属性：权限控制
  get hasCreatePermission() {
    return HasPer(['漏点检测', '漏点管理', '上帝'])
  }

  get hasBatchOperationPermission() {
    return HasPer(['漏点管理', '上帝']) && !this.isMobile
  }

  // 显示批量操作
  private showBatchOperations() {
    if (this.$refs.leakListRef) {
      (this.$refs.leakListRef as any).showBatchOperations = true
    }
  }

  // 处理创建漏点 - 使用页面导航代替弹窗
  private navigateToCreatePage() {
    // 添加检测人员列表参数到路由的params中，使用命名路由
    this.$router.push({
      name: 'CreateLeak',
      params: {
        detectUsers: JSON.stringify(this.detectUsers)
      }
    })
  }

  // 旧的处理创建漏点方法，保留备用
  private handleCreate() {
    this.createTemp = {
      location: { longitude: 0, latitude: 0 },
      referenceAddr: ''
    }
    this.createDialogVisible = true
  }

  // 处理创建对话框关闭
  private handleCreateDialogClose(visible: boolean) {
    if (!visible) {
      // 当创建对话框关闭时刷新列表
      this.refreshList()
      // 更新统计卡片数据
      this.updateStatCards()
    }
  }

  // 处理批量分配
  private async handleBatchAssign(data: { leakIds: number[], deptId: string | number }) {
    if (!data.leakIds.length) {
      this.$message({
        message: '没有可分配的漏点',
        type: 'warning'
      })
      return
    }

    try {
      // 显示加载提示
      this.$message({
        message: '正在批量分配漏点...',
        type: 'info'
      })

      // 获取漏点详情以验证状态
      // const { data: leakData } = await ListLeaks({
      //   status: [LeakStatus.NeedAssignRepairDept], // 只查询待分配状态的漏点
      //   page: 1,
      //   pageSize: 30 // 设置一个足够大的值，确保获取所有匹配的漏点
      // })

      // 筛选出当前仍处于待分配状态的漏点
      // const validLeakIds = this.filter(id =>
      //   leakData.items.some((leak: any) => leak.id === id && leak.leakStatus === LeakStatus.NeedAssignRepairDept)
      // )
      const validLeakIds = data.leakIds

      if (!validLeakIds.length) {
        this.$message({
          message: '所选漏点已不处于待分配状态，无法执行批量分配',
          type: 'warning'
        })
        return
      }

      // 调用批量分配API
      await BatchAssignRepairDept({
        leakIds: validLeakIds,
        deptId: Number(data.deptId)
      })

      // 操作成功提示
      this.$message({
        message: `成功将${validLeakIds.length}个漏点分配给维修部门`,
        type: 'success'
      })

      // 刷新列表
      this.refreshList()

      // 更新统计卡片数据
      this.updateStatCards()
    } catch (error) {
      // 处理错误
      console.error('批量分配漏点失败', error)
      this.$message({
        message: '批量分配失败，请重试',
        type: 'error'
      })
    }
  }

  // 搜索参数
  private searchParams = {
    status: [] as number[],
    keywords: [''] as string[],
    addressKeywords: [''] as string[],
    createTimeStart: '',
    createTimeEnd: '',
    reportTimeStart: '',
    reportTimeEnd: '',
    page: 1,
    pageSize: 30
  }

  // 统计卡片数据
  private statCards: CardData[] = []
  private selectedCardId: string = '' // 添加选中卡片的ID

  // 选项数据
  private deptList: IAllDeptInfo[] = []
  private userList: IAllUserInfo[] = []
  private projectList: IProject[] = []

  // 操作按钮配置
  private actionConfig = {
    showDetail: true,
    showEdit: false,
    showReport: true,
    showAssign: false,
    showMarkNoNeed: false,
    showComplete: false
  }

  created() {
    // 初始化数据
    this.getOptions()

    // 设置默认年度筛选（当前年份）
    const currentYear = new Date().getFullYear()
    this.searchParams.createTimeStart = `${currentYear}-01-01T00:00:00Z`

    // 获取今天的日期作为结束时间
    const today = new Date()
    const todayString = today.toISOString().split('T')[0]
    this.searchParams.createTimeEnd = `${todayString}T23:59:59Z`

    // 根据用户权限设置默认显示的漏点状态
    // 如果用户有漏点检测或上帝权限，显示所有漏点；否则不显示未上报的漏点
    if (HasPer(['漏点检测', '上帝'])) {
      // 显示所有状态的漏点
      this.searchParams.status = [
        LeakStatus.Unreported,
        LeakStatus.NeedAssignRepairDept,
        LeakStatus.NeedAssignRepairUser,
        LeakStatus.Repairing,
        LeakStatus.Repaired,
        LeakStatus.CantRepair,
        LeakStatus.NoNeedRepair
      ]
    } else {
      // 不显示未上报的漏点
      this.searchParams.status = [
        LeakStatus.NeedAssignRepairDept,
        LeakStatus.NeedAssignRepairUser,
        LeakStatus.Repairing,
        LeakStatus.Repaired,
        LeakStatus.CantRepair,
        LeakStatus.NoNeedRepair
      ]
    }

    // 初始化统计卡片
    this.initStatCards()

    // 监听窗口大小变化
    window.addEventListener('resize', this.handleResize)
  }

  mounted() {
    // 组件挂载后更新统计卡片数据
    this.updateStatCards()
  }

  destroyed() {
    window.removeEventListener('resize', this.handleResize)
  }

  // 处理窗口大小变化
  private handleResize() {
    this.$forceUpdate()
  }

  private initDetecttionCards() {
    if (HasPer(['漏点检测'])) {
      console.log("初始化检测人员漏点卡片:", UserModule.permissions)
      const currentYear = new Date().getFullYear()
      const yearStart = `${currentYear}-01-01`

      // 未上报漏点卡片
      this.statCards.push({
        id: 'unreported',
        title: '未上报',
        filterParams: {
          status: [LeakStatus.Unreported],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.Unreported)
      })

      // 已上报漏点卡片（除未上报外的所有状态）
      this.statCards.push({
        id: 'reported',
        title: '已上报',
        filterParams: {
          status: [
            LeakStatus.NeedAssignRepairDept,
            LeakStatus.NeedAssignRepairUser,
            LeakStatus.Repairing,
            LeakStatus.Repaired,
            LeakStatus.CantRepair,
            LeakStatus.NoNeedRepair
          ],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.Repairing)
      })

      // 所有漏点卡片
      this.statCards.push({
        id: 'all',
        title: '全部',
        filterParams: {
          status: [
            LeakStatus.Unreported,
            LeakStatus.NeedAssignRepairDept,
            LeakStatus.NeedAssignRepairUser,
            LeakStatus.Repairing,
            LeakStatus.Repaired,
            LeakStatus.CantRepair,
            LeakStatus.NoNeedRepair
          ],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: '#303133' // 黑色
      })
    }
  }

  private initRepairCards() {
    if (HasPer(['漏点维修'])) {
      console.log("初始化维修人员漏点卡片:", UserModule.permissions)
      const currentYear = new Date().getFullYear()
      const yearStart = `${currentYear}-01-01`

      // 未认领漏点卡片
      this.statCards.push({
        id: 'unassigneRepairUser',
        title: '未认领',
        filterParams: {
          status: [LeakStatus.NeedAssignRepairUser],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.NeedAssignRepairUser)
      })

      // 维修中的漏点卡片
      this.statCards.push({
        id: 'repairing',
        title: '维修中',
        filterParams: {
          status: [
            LeakStatus.Repairing,
          ],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.Repairing)
      })

      // 所有漏点卡片
      this.statCards.push({
        id: 'all',
        title: '已完成',
        filterParams: {
          status: [
            LeakStatus.Repaired,
            LeakStatus.CantRepair,
            LeakStatus.NoNeedRepair
          ],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.Repaired)
      })
    }
  }

  private initManagerCards() {
    if (HasPer(['漏点管理'])) {
      console.log("初始化管理人员漏点卡片:", UserModule.permissions)
      const currentYear = new Date().getFullYear()
      const yearStart = `${currentYear}-01-01`

      // 未分配漏点卡片
      this.statCards.push({
        id: 'unassigneRepairDept',
        title: '未分配',
        filterParams: {
          status: [LeakStatus.NeedAssignRepairDept],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.NeedAssignRepairDept)
      })

      // 未认领和维修中的漏点卡片
      this.statCards.push({
        id: 'repairing',
        title: '已分配',
        filterParams: {
          status: [
            LeakStatus.Repairing,
            LeakStatus.NeedAssignRepairUser
          ],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.Repairing)
      })

      // 已完成
      this.statCards.push({
        id: 'all',
        title: '已完结',
        filterParams: {
          status: [
            LeakStatus.Repaired,
            LeakStatus.CantRepair,
            LeakStatus.NoNeedRepair
          ],
          keywords: [''],
          addressKeywords: [''],
          createTimeStart: `${yearStart}T00:00:00Z`,
          createTimeEnd: this.searchParams.createTimeEnd,
          page: 1,
          pageSize: 10
        },
        todayCount: 0,
        yesterdayCount: 0,
        startDate: yearStart,
        color: GetLeakStatusColor(LeakStatus.Repaired)
      })
    }
  }

  // 初始化统计卡片
  private initStatCards() {
    this.initDetecttionCards()
    this.initRepairCards()
    this.initManagerCards()

    // 默认不选中任何卡片
    this.selectedCardId = ''

    // 设置默认的搜索参数
    if (this.statCards.length > 0) {
      // this.searchParams = { ...this.statCards[0].filterParams }
      this.refreshList()
    }
  }

  private async getOptions() {
    await this.getDeptList()
    await this.getUserList()
    await this.getProjectList()
  }

  private async getDeptList() {
    try {
      const response = await ListDepts()
      this.deptList = (response.data || [])
    } catch (error) {
      console.error('获取部门列表失败', error)
    }
  }

  // 只能自己创建漏点
  private get detectUsers() : IAllUserInfo[] {
    return this.userList.filter(user => user.user.id === UserModule.userInfo?.id)
  }

  private get repairUsers() : IAllUserInfo[] {
    return this.userList.filter(user => user.permissions.includes('漏点维修'))
  }

  private get repairDepts() : IDept [] {
    return this.deptList.filter(dept => dept.dept.type === DeptType.Repair).map(dept=>dept.dept)
  }

  private async getUserList() {
    try {
      const { data } = await ListOrgUsers()
      this.userList = data.items || []
    } catch (error) {
      console.error('获取用户列表失败', error)
    }
  }

  private async getProjectList() {
    const { data } = await ListProjects()
    this.projectList = data.map((project:any) => project.project)
  }

  // 刷新列表
  private refreshList() {
    this.shouldReloadList = false
    this.$nextTick(() => {
      this.shouldReloadList = true
    })

    // 刷新统计卡片数据
    this.updateStatCards()
  }

  // 更新统计卡片数据
  private async updateStatCards() {
    try {
      // 遍历每个卡片，获取最新的统计数据
      for (let i = 0; i < this.statCards.length; i++) {
        // 获取卡片引用
        const cardRef = this.$refs[`cardRef${i}`] as any

        // 如果卡片组件存在，则调用其 fetchCardData 方法更新数据
        if (cardRef && cardRef[0] && typeof cardRef[0].fetchCardData === 'function') {
          await cardRef[0].fetchCardData()
        } else {
          // 如果无法直接通过组件更新，则使用之前的方法
          const card = this.statCards[i]

          // 获取今天的统计数据
          const todayStart = new Date()
          todayStart.setHours(0, 0, 0, 0)
          const todayEnd = new Date()
          todayEnd.setHours(23, 59, 59, 999)

          // 获取昨天的统计数据
          const yesterdayStart = new Date()
          yesterdayStart.setDate(yesterdayStart.getDate() - 1)
          yesterdayStart.setHours(0, 0, 0, 0)
          const yesterdayEnd = new Date()
          yesterdayEnd.setDate(yesterdayEnd.getDate() - 1)
          yesterdayEnd.setHours(23, 59, 59, 999)

          // 获取今天的数据
          const todayParams = {
            ...card.filterParams,
            createTimeStart: todayStart.toISOString(),
            createTimeEnd: todayEnd.toISOString(),
            page: 1,
            pageSize: 1 // 只需要总数，所以pageSize设为最小值
          }

          // 获取昨天的数据
          const yesterdayParams = {
            ...card.filterParams,
            createTimeStart: yesterdayStart.toISOString(),
            createTimeEnd: yesterdayEnd.toISOString(),
            page: 1,
            pageSize: 1 // 只需要总数，所以pageSize设为最小值
          }

          // 调用API获取今天的统计数据
          const todayResponse = await ListLeaks(todayParams)
          // 调用API获取昨天的统计数据
          const yesterdayResponse = await ListLeaks(yesterdayParams)

          // 更新卡片数据
          card.todayCount = todayResponse.data?.total || 0
          card.yesterdayCount = yesterdayResponse.data?.total || 0

          // 使用Vue的响应式更新方法更新数组元素
          this.$set(this.statCards, i, card)
        }
      }
    } catch (error) {
      console.error('更新统计卡片数据失败', error)
    }
  }

  // 处理搜索条件变化
  private handleFilterChange(params: any) {
    console.log('搜索条件变化:', params)
    this.searchParams = { ...params }
    this.refreshList()
  }

  // 处理搜索条件重置
  private handleFilterReset() {
    console.log('搜索条件已重置')
    // 由于重置也会触发filter-change事件，这里不需要额外处理
  }

  // 处理卡片点击
  private handleCardClick(params: any) {
    console.log('卡片点击:', params)
    // 更新选中的卡片ID
    this.selectedCardId = params.cardId || ''
    // 更新搜索参数
    this.searchParams = { ...params }
    // 刷新列表
    this.refreshList()
  }

  // 处理保存卡片
  private handleSaveCard(cardData: CardData) {
    console.log('保存卡片:', cardData)
    // 查找是否存在相同ID的卡片
    const index = this.statCards.findIndex(card => card.id === cardData.id)
    if (index !== -1) {
      // 更新现有卡片
      this.$set(this.statCards, index, cardData)
    } else {
      // 添加新卡片
      this.statCards.push(cardData)
    }
  }

  // 处理详情
  private handleDetail(leakId: number, operationType?: string) {
    if (!leakId) {
      this.$message.warning('无效的漏点ID')
      return
    }

    // 根据漏点ID获取相关信息
    const mode = operationType || 'view'

    // 导航到详情页面，使用命名路由
    this.$router.push({
      name: 'LeakDetail',
      params: {
        id: leakId.toString(),
        mode: mode,
        projects: JSON.stringify(this.projectList),
        checkUsers: JSON.stringify(this.detectUsers),
        repairUsers: JSON.stringify(this.repairUsers),
        repairDepts: JSON.stringify(this.repairDepts),
      }
    })
  }

  // 处理上报
  private handleReport(leakId: number) {
    this.handleDetail(leakId, 'report')
  }

  // 查找漏点信息
  private findLeakInList(leakId: number) {
    if (!this.$refs.leakListRef) return null

    const leakList = (this.$refs.leakListRef as any).leakList
    if (!leakList || !leakList.length) return null

    return leakList.find((item: any) => item.id === leakId)
  }
}
